/***********************************************

	"diff.js"

	Created by Michael Cheng on 07/22/2016 09:55
						http://michaelcheng.us/
						michael@michaelcheng.us
						--All Rights Reserved--


	@see https://github.com/mlcheng/js-diff/blob/master/diff.js
***********************************************/

'use strict'

const MAX_DISTANCE = Number.MAX_SAFE_INTEGER

export const SplitBy = {
  CHAR: '',
  // do not support word and line
  // WORD: '(\\s)',
  // LINE: '(\\n)'
}

const DEFAULT_SPLITTER = SplitBy.CHAR

export interface LcsDiffChangeResult {
  char?: string
  diff?:
    | '='
    | '+'
    | '-'
    | {
        replacedBy: string
      }
}

export function lcsDiffChanges(
  from: string,
  to: string,
  splitBy: string = DEFAULT_SPLITTER
): LcsDiffChangeResult[] {
  const splitByFrom = from.split(new RegExp(splitBy, 'g'))
  const splitByTo = to.split(new RegExp(splitBy, 'g'))

  // Dynamic programming matrix
  const steps: number[][] = []

  // Initialize the matrix that shows the steps needed to make each change
  for (let i = 0; i <= splitByFrom.length; i++) {
    steps[i] = []
    for (let j = 0; j <= splitByTo.length; j++) {
      if (i === 0) steps[i][j] = j
      else if (j === 0) steps[i][j] = i
      else steps[i][j] = 0
    }
  }

  // Build the Levenshtein distance matrix
  {
    let left, diagonal, up
    for (let i = 1; i <= splitByFrom.length; i++) {
      for (let j = 1; j <= splitByTo.length; j++) {
        left = steps[i][j - 1]
        diagonal = steps[i - 1][j - 1]
        up = steps[i - 1][j]
        if (from[i - 1] === to[j - 1]) {
          // Current characters are same, so inherit from last LCS (which is diagonal)
          steps[i][j] = diagonal
        } else {
          // Another difference, so add one to the last optimal LCS
          steps[i][j] = Math.min(left, diagonal, up) + 1
        }
      }
    }
  }

  //Traverse the matrix to get the differences
  const changes: LcsDiffChangeResult[] = []
  {
    let i = from.length,
      j = to.length,
      left,
      diagonal,
      up,
      source,
      char,
      diff: LcsDiffChangeResult['diff']
    while (i > 0 || j > 0) {
      if (from[i - 1] === to[j - 1]) {
        // No changes from source to destination
        ;(char = from[i - 1]), (diff = '=')
        i--
        j--
      } else {
        if (i - 1 < 0) {
          up = MAX_DISTANCE
        } else {
          up = steps[i - 1][j]
        }
        if (j - 1 < 0) {
          left = MAX_DISTANCE
        } else {
          left = steps[i][j - 1]
        }
        if (i - 1 < 0 || j - 1 < 0) {
          diagonal = MAX_DISTANCE
        } else {
          diagonal = steps[i - 1][j - 1]
        }
        source = Math.min(left, up, diagonal)
        if (source === left) {
          // New character from destination
          ;(char = to[j - 1]), (diff = '+')
          j--
        } else if (source === up) {
          // Removed character from source
          ;(char = from[i - 1]), (diff = '-')
          i--
        } else if (source === diagonal) {
          // Destination character replace source
          ;(char = from[i - 1]),
            (diff = {
              replacedBy: to[j - 1],
            })
          i--
          j--
        }
      }
      changes.push({ char, diff })
    }
  }

  return changes.reverse()
}
